import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Stack,
  Typography,
} from "@mui/material";
import { useCallback, useEffect, useState } from "react";
import { useOutletContext } from "react-router-dom";
import { tokens } from "../../theme";
import { foyerApi, type OwnerShell } from "../../http/foyer";
import { PageHeader } from "../../components/PageHeader";
import {
  AGREEMENT_LABEL,
  type AgreementStatus,
} from "@shared/content-agreement";

/**
 * Agreements — what a supplier has signed, and what they still owe
 * (`CONTENT-19`).
 *
 * ## The document is on the page, not behind a link
 *
 * `GET /owner/agreements` returns each document's `body` with its status, and
 * this page renders it where the Accept button is. That is the server's design
 * and it is load-bearing: a supplier must be able to READ what they are
 * accepting on the screen where they accept it, and a link to a page somebody
 * can edit is exactly what the stored artefact plus checksum exists to prevent.
 *
 * ## One button per document, deliberately
 *
 * There is no "accept all". Five documents behind one click produces a record
 * saying somebody clicked — not that somebody agreed to five things. The route
 * takes a single `agreementId` for the same reason.
 *
 * ## A superseded acceptance is not an acceptance
 *
 * `supersededAcceptance` means they signed an EARLIER version of this document.
 * It renders as outstanding-with-history rather than as a tick, because the
 * terms in force are the ones they have not agreed to — and a supplier who sees
 * a tick will not look again.
 */

interface ShellContext {
  shell: OwnerShell;
}

export default function SupplierAgreementsPage() {
  useOutletContext<ShellContext>();
  const [status, setStatus] = useState<AgreementStatus | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setStatus(await foyerApi.ownerAgreements());
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const accept = async (agreementId: string) => {
    setBusy(agreementId);
    setError(null);
    try {
      setStatus(await foyerApi.acceptAgreement(agreementId));
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(null);
    }
  };

  if (error) return <Alert severity="error">{error}</Alert>;
  if (!status)
    return (
      <Box sx={{ display: "grid", placeItems: "center", py: 8 }}>
        <CircularProgress size={26} />
      </Box>
    );

  return (
    <Stack spacing={3}>
      <PageHeader
        eyebrow="Your account"
        title="Agreements"
        subtitle="The terms your material is carried under. Each is shown in full here — you are agreeing to what is on this page."
      />

      {status.agreements.length === 0 ? (
        <Box sx={{ py: 5, textAlign: "center" }}>
          <Typography sx={{ fontSize: 20, mb: 1 }}>Nothing to sign</Typography>
          <Typography
            sx={{ color: tokens.inkMuted, maxWidth: 460, mx: "auto" }}
          >
            Nenda has not published any terms for suppliers yet. When we do,
            they appear here and we will tell you.
          </Typography>
        </Box>
      ) : status.satisfied ? (
        <Alert severity="success">
          You are up to date — everything currently in force has been accepted.
        </Alert>
      ) : (
        /* 🚨 The count is the headline. An outstanding document is the thing
           that stops material going live, and a supplier who has to count the
           rows themselves will not. */
        <Alert severity="warning" data-testid="agreements-outstanding">
          {status.outstanding.length} document
          {status.outstanding.length === 1 ? "" : "s"} still need your
          agreement.
        </Alert>
      )}

      <Stack spacing={2}>
        {status.agreements.map((row) => {
          const accepted = row.acceptedAt !== null && !row.supersededAcceptance;
          return (
            <Box
              key={row.agreement.id}
              sx={{
                background: tokens.surface,
                border: `1px solid ${tokens.divider}`,
                borderRadius: 2,
                p: 2.5,
              }}
              data-testid={`agreement-${row.agreement.kind}`}
            >
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="flex-start"
                sx={{ mb: 1 }}
              >
                <Box>
                  <Typography sx={{ fontWeight: 600 }}>
                    {AGREEMENT_LABEL[row.agreement.kind]}
                  </Typography>
                  <Typography variant="caption" sx={{ color: tokens.inkFaint }}>
                    Version {row.agreement.version} · in force from{" "}
                    {new Date(row.agreement.effectiveFrom).toLocaleDateString()}
                  </Typography>
                </Box>
                {accepted ? (
                  <Chip
                    size="small"
                    color="primary"
                    label={`Accepted ${new Date(row.acceptedAt!).toLocaleDateString()}`}
                  />
                ) : (
                  <Button
                    variant="contained"
                    size="small"
                    disabled={busy !== null}
                    onClick={() => void accept(row.agreement.id)}
                    data-testid={`agreement-accept-${row.agreement.kind}`}
                  >
                    Accept
                  </Button>
                )}
              </Stack>

              {/* 🚨 Superseded is NOT accepted. They agreed to an older text,
                  and the terms in force are ones they have not seen. Shown as
                  history under an Accept button, never as a tick. */}
              {row.supersededAcceptance ? (
                <Alert severity="info" sx={{ mb: 1.5 }}>
                  You accepted an earlier version of this document. This one has
                  changed and needs agreeing again.
                </Alert>
              ) : null}

              <Box
                sx={{
                  maxHeight: 260,
                  overflowY: "auto",
                  background: tokens.canvas,
                  border: `1px solid ${tokens.divider}`,
                  borderRadius: 1,
                  p: 2,
                  whiteSpace: "pre-wrap",
                  fontSize: 13,
                  color: tokens.inkMuted,
                }}
                data-testid={`agreement-body-${row.agreement.kind}`}
              >
                {row.agreement.body}
              </Box>
            </Box>
          );
        })}
      </Stack>
    </Stack>
  );
}
